/**
 * Customer groups and sales reps, each list edited in place: add, edit,
 * switch off, delete. Deleting one leaves its customers without a group
 * (or rep), never deletes them. Which customer is in which is set on the
 * Customers tab.
 */

import { useState } from "react";
import PropTypes from "prop-types";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import RowActions from "components/RowActions";
import { api, problemOf } from "./api";

/** The boxes each list asks for; the first is the name, and is required. */
const FIELDS = {
  groups: [{ name: "name", placeholder: "New group..." }],
  reps: [
    { name: "name", placeholder: "New rep..." },
    { name: "phone", placeholder: "Phone", width: 140 },
    { name: "commission_percent", placeholder: "Commission %", width: 120, type: "number" },
  ],
};

const blank = (fields) => Object.fromEntries(fields.map((f) => [f.name, ""]));

/** A row's values as the form holds them - text, never null. */
const draftOf = (fields, row) => Object.fromEntries(fields.map((f) => [f.name, row[f.name] ?? ""]));

function DefinitionList({ path, title, noun, rows, onChanged }) {
  const fields = FIELDS[path];
  const [draft, setDraft] = useState(() => blank(fields));
  // {id, ...values} while a row is being edited
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");

  const run = async (fn) => {
    setError("");
    try {
      await fn();
      onChanged();
      return true;
    } catch (e) {
      setError(problemOf(e, "Could not save"));
      return false;
    }
  };

  const add = async () => {
    if (!draft.name.trim()) return;
    if (await run(() => api.post(`/${path}`, draft))) setDraft(blank(fields));
  };

  const save = async () => {
    const row = rows.find((r) => r.id === editing.id);
    if (!editing.name.trim() || !row) return;
    const { id, ...values } = editing;
    if (await run(() => api.put(`/${path}/${id}`, { ...values, is_active: row.is_active })))
      setEditing(null);
  };

  const remove = (row) => {
    const note = row.customers
      ? ` ${row.customers} customer(s) will be left without a ${noun}.`
      : "";
    // eslint-disable-next-line no-alert
    if (window.confirm(`Delete "${row.name}"?${note}`)) run(() => api.del(`/${path}/${row.id}`));
  };

  const boxes = (values, onChange, onEnter) =>
    fields.map((f) => (
      <TextField
        key={f.name}
        size="small"
        type={f.type || "text"}
        placeholder={f.placeholder}
        value={values[f.name]}
        onChange={(e) => onChange({ ...values, [f.name]: e.target.value })}
        onKeyDown={(e) => e.key === "Enter" && onEnter()}
        sx={f.width ? { width: f.width, flexShrink: 0 } : { flex: 1, minWidth: 140 }}
      />
    ));

  return (
    <SuiBox
      flex="1 1 420px"
      p={2}
      sx={({ palette, borders, functions: { rgba } }) => ({
        border: `1px solid ${rgba(palette.dark.main, 0.1)}`,
        borderRadius: borders.borderRadius.lg,
      })}
    >
      <SuiTypography variant="h6" mb={1.5}>
        {title}
      </SuiTypography>

      <SuiBox display="flex" gap={1} mb={1.5} flexWrap="wrap">
        {boxes(draft, setDraft, add)}
        <Button variant="contained" color="primary" onClick={add}>
          Add
        </Button>
      </SuiBox>

      {error && (
        <SuiTypography variant="caption" color="error" display="block" mb={1}>
          {error}
        </SuiTypography>
      )}

      {rows.map((r) => (
        <SuiBox
          key={r.id}
          display="flex"
          alignItems="center"
          gap={1}
          py={0.75}
          sx={({ palette, functions: { rgba } }) => ({
            borderBottom: `1px solid ${rgba(palette.dark.main, 0.06)}`,
          })}
        >
          {editing?.id === r.id ? (
            <SuiBox display="flex" gap={1} flex={1} flexWrap="wrap">
              {boxes(editing, setEditing, save)}
              <Button variant="contained" color="primary" size="small" onClick={save}>
                Save
              </Button>
              <Button variant="outlined" color="dark" size="small" onClick={() => setEditing(null)}>
                Cancel
              </Button>
            </SuiBox>
          ) : (
            <SuiTypography
              sx={{ flex: 1, fontSize: 14, fontWeight: 600, opacity: r.is_active ? 1 : 0.45 }}
            >
              {r.name}
              <SuiTypography component="span" sx={{ fontSize: 12, opacity: 0.55, ml: 1 }}>
                {[
                  r.phone,
                  r.commission_percent !== null && r.commission_percent !== undefined
                    ? `${Number(r.commission_percent)}%`
                    : "",
                  r.customers ? `${r.customers} customers` : "",
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </SuiTypography>
            </SuiTypography>
          )}
          <Tooltip title={r.is_active ? "Active - offered on the Customers tab" : "Switched off"}>
            <Switch
              size="small"
              checked={r.is_active}
              onChange={(e) =>
                run(() =>
                  api.put(`/${path}/${r.id}`, {
                    ...draftOf(fields, r),
                    is_active: e.target.checked,
                  })
                )
              }
            />
          </Tooltip>
          <RowActions
            actions={[
              {
                icon: "edit",
                label: "Edit",
                onClick: () => setEditing({ id: r.id, ...draftOf(fields, r) }),
              },
              { icon: "delete", label: "Delete", tone: "danger", onClick: () => remove(r) },
            ]}
          />
        </SuiBox>
      ))}
      {rows.length === 0 && (
        <SuiTypography variant="caption" sx={{ opacity: 0.6 }}>
          None yet
        </SuiTypography>
      )}
    </SuiBox>
  );
}

DefinitionList.propTypes = {
  path: PropTypes.oneOf(["groups", "reps"]).isRequired,
  title: PropTypes.string.isRequired,
  noun: PropTypes.string.isRequired,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  onChanged: PropTypes.func.isRequired,
};

function GroupsTab({ groups, reps, onChanged }) {
  return (
    <SuiBox px={3} py={2}>
      <SuiTypography variant="h6" mb={2}>
        Groups & Reps · المجموعات والمندوبين
      </SuiTypography>
      <SuiBox display="flex" gap={3} flexWrap="wrap">
        <DefinitionList
          path="groups"
          title="Customer groups · مجموعات الزبائن"
          noun="group"
          rows={groups}
          onChanged={onChanged}
        />
        <DefinitionList
          path="reps"
          title="Sales reps · المندوبين"
          noun="rep"
          rows={reps}
          onChanged={onChanged}
        />
      </SuiBox>
    </SuiBox>
  );
}

GroupsTab.propTypes = {
  groups: PropTypes.arrayOf(PropTypes.object).isRequired,
  reps: PropTypes.arrayOf(PropTypes.object).isRequired,
  onChanged: PropTypes.func.isRequired,
};

export default GroupsTab;
